/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Retry strategy", description = "How Hightouch retries records a destination rejects, and how to recover when a record keeps failing.", lastUpdatedDate = new Date(1787097600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Retry strategy",
        description: "How Hightouch retries records a destination rejects, and how to recover when a record keeps failing.",
        lastUpdatedDate: "Wed Aug 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "How Hightouch retries records",
          "depth": 0,
          "id": "how-hightouch-retries-records"
        }, {
          "value": "Example",
          "depth": 1,
          "id": "example"
        }, {
          "value": "Rejected rows don't mean a failed run",
          "depth": 0,
          "id": "rejected-rows-dont-mean-a-failed-run"
        }, {
          "value": "Split retries",
          "depth": 0,
          "id": "split-retries"
        }, {
          "value": "When to step in",
          "depth": 0,
          "id": "when-to-step-in"
        }, {
          "value": "A record keeps getting rejected",
          "depth": 1,
          "id": "a-record-keeps-getting-rejected"
        }, {
          "value": "Hightouch keeps retrying a removed row",
          "depth": 1,
          "id": "hightouch-keeps-retrying-a-removed-row"
        }, {
          "value": "A whole run failed",
          "depth": 1,
          "id": "a-whole-run-failed"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      em: "em",
      h2: "h2",
      strong: "strong",
      a: "a",
      h3: "h3",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "When a destination rejects a record, Hightouch retries it automatically — during the run and on later runs — until it succeeds or you fix the underlying problem."
        })
      }), "\n", _jsx(_components.h2, {
        id: "how-hightouch-retries-records",
        children: "How Hightouch retries records"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch retries rejected records at two levels, both automatic."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "During a run."
        }), " When a destination rejects a record, Hightouch retries it within the same run using exponential backoff, up to a set number of attempts (five by default; some destinations set their own limit). Not every error is retried this way. A request the destination rejects as invalid — for example, a 400, 401, 403, or 422 response — is marked rejected immediately instead of retried. Rate-limited requests (429) get a larger retry budget."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "On later runs."
        }), " Hightouch keeps an index of every rejected record, keyed by its ", _jsx(_components.a, {
          href: "/models/creating-models#unique-primary-key-requirement",
          children: "primary key"
        }), ". It retries each one on every subsequent run until the record succeeds or drops out of your model's query results. This layer has no attempt limit, so a record with a persistent problem retries indefinitely until you fix it."]
      }), "\n", _jsx(_components.h3, {
        id: "example",
        children: "Example"
      }), "\n", _jsx(_components.p, {
        children: "Say you sync 10,000 records to Salesforce and five are locked in Salesforce when the run starts."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "If two unlock during the run, Hightouch's in-run retries update them before the run ends."
        }), "\n", _jsx(_components.li, {
          children: "Hightouch retries the remaining three on the next run. Once they unlock, they update. Hightouch keeps retrying rejected records until they succeed."
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "rejected-rows-dont-mean-a-failed-run",
        children: "Rejected rows don't mean a failed run"
      }), "\n", _jsxs(_components.p, {
        children: ["A run that finishes with rejected records ", _jsx(_components.strong, {
          children: "completes with errors"
        }), " rather than failing. The run finished and synced the rows it could, but not every row landed. In the run list, this shows as ", _jsx(_components.strong, {
          children: "Completed with errors"
        }), ", a warning-level status distinct from a failure. A run is marked ", _jsx(_components.strong, {
          children: "Failed"
        }), " only when the whole run errors, such as a query failure, an authentication error, or a destination error that stops it before rows process."]
      }), "\n", _jsxs(_components.p, {
        children: ["So a completed run doesn't guarantee that every record synced. When you troubleshoot, check both the run status and the rejected-row count in the run's ", _jsx(_components.strong, {
          children: "Results"
        }), " column or the ", _jsx(_components.a, {
          href: "/syncs/debugger",
          children: "live debugger"
        }), ". Set up ", _jsx(_components.a, {
          href: "/syncs/alerting",
          children: "alerting"
        }), " to be notified when rows are rejected. The ", _jsx(_components.strong, {
          children: "Rejected rows"
        }), " alert fires when a run rejects one or more rows."]
      }), "\n", _jsx(_components.h2, {
        id: "split-retries",
        children: "Split retries"
      }), "\n", _jsxs(_components.p, {
        children: ["Some destinations reject an entire batch when it contains even one invalid record. For those, you can enable ", _jsx(_components.strong, {
          children: "split retries"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "With split retries on, Hightouch splits a rejected batch in half and retries each half separately. If a half still fails, Hightouch splits it again, and repeats until the destination marks each record individually as successful or rejected. Splitting stops after a set number of rounds."
      }), "\n", _jsx(_components.p, {
        children: "Split retries pinpoint which records are rejected and why, and they keep valid records from being re-sent alongside invalid ones. Records still rejected at the end of the process are retried on the next run."
      }), "\n", _jsxs(_components.p, {
        children: ["To turn them on, set ", _jsx(_components.strong, {
          children: "Would you like to enable split retries?"
        }), " to ", _jsx(_components.strong, {
          children: "Yes"
        }), " in the sync configuration. To check whether a destination supports split retries, see its documentation."]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Split retries can lengthen sync times, because a failing batch is retried in\nprogressively smaller pieces."
        })
      }), "\n", _jsx(_components.h2, {
        id: "when-to-step-in",
        children: "When to step in"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch recovers from transient problems on its own. Step in when a record keeps failing for a reason retries can't resolve."
      }), "\n", _jsx(_components.h3, {
        id: "a-record-keeps-getting-rejected",
        children: "A record keeps getting rejected"
      }), "\n", _jsxs(_components.p, {
        children: ["If a record holds data the destination won't accept — a malformed email, a value that breaks a destination rule — Hightouch retries it every run and it keeps getting rejected. Fix the data at the source: ", _jsx(_components.a, {
          href: "/models/sql-editor",
          children: "edit your model"
        }), " to correct the value or filter the row out of the query results. What counts as valid depends on the destination."]
      }), "\n", _jsx(_components.h3, {
        id: "hightouch-keeps-retrying-a-removed-row",
        children: "Hightouch keeps retrying a removed row"
      }), "\n", _jsxs(_components.p, {
        children: ["If you set ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " to clear or delete removed records, Hightouch sends a clear or delete request when a row leaves your model's query results. If that request fails — the destination rejects it, or its API is unavailable — Hightouch retries it on the next run."]
      }), "\n", _jsx(_components.p, {
        children: "To stop retrying a removed row, choose one option:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Set delete behavior to ", _jsx(_components.strong, {
            children: "Do nothing"
          }), ". This applies to every removed row, not only the one being retried."]
        }), "\n", _jsxs(_components.li, {
          children: ["Temporarily set delete behavior to ", _jsx(_components.strong, {
            children: "Do nothing"
          }), ", run the sync ", _jsx(_components.a, {
            href: "/syncs/schedule-sync-ui#manual-runs",
            children: "manually"
          }), " once to clear the removed rows from the ", _jsx(_components.a, {
            href: "/syncs/cdc",
            children: "CDC diff"
          }), ", then set delete behavior back."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/syncs/resync-reset-clear#reset-cdc",
            children: "Reset change data capture"
          }), " without a backfill."]
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["The last two options skip data. During the ", _jsx(_components.strong, {
            children: "Do nothing"
          }), " run, Hightouch\ndoesn't sync any rows CDC marks as removed. A reset-CDC run doesn't sync any\nmodel changes — added, changed, or removed rows — made since the previous run.\nTo reduce the risk of missing changes, run a normal sync right before you use\neither option."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "a-whole-run-failed",
        children: "A whole run failed"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.strong, {
          children: "failed"
        }), " run means the run errored as a whole, not that individual records were rejected. Open the run in the ", _jsx(_components.a, {
          href: "/syncs/debugger",
          children: "live debugger"
        }), " to see the error, then look it up in ", _jsx(_components.a, {
          href: "/developer-tools/error-codes",
          children: "error codes"
        }), " for the cause and fix."]
      })]
    });
  }
}
export default MDXContent;
